import { useBranding } from '@/contexts/BrandingContext';
import { ArrowLeft, Building2, Shield } from 'lucide-react';
import { Link } from 'react-router-dom';

import ChatWidgetLoader from '@/components/ChatWidgetLoader';

export default function PrivacyPolicy() {
  const { branding } = useBranding();

  return (
    <div className="min-h-screen bg-gray-50">
      <ChatWidgetLoader />
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            {branding.logo_url ? (
              <img src={branding.logo_url} alt={branding.platform_name} className="h-8 object-contain" />
            ) : (
              <div className="p-2 bg-violet-600 rounded-lg">
                <Building2 className="w-5 h-5 text-white" />
              </div>
            )}
            <span className="font-semibold text-gray-900">{branding.platform_name}</span>
          </Link>
          <Link 
            to="/" 
            className="flex items-center gap-2 text-gray-600 hover:text-violet-600 transition-colors"
            data-testid="back-to-home"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Back to Home</span>
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 py-12">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 md:p-12">
          <div className="flex items-center gap-3 mb-8">
            <div className="p-3 bg-green-100 rounded-lg">
              <Shield className="w-6 h-6 text-green-600" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900" data-testid="privacy-title">Privacy Policy</h1>
          </div>
          
          <div className="prose prose-gray max-w-none">
            <p className="text-gray-600 mb-8">
              Trackly.net is committed to protecting your privacy and personal data. This policy explains how we collect, use, store, and protect information.
            </p>

            <h2 className="text-xl font-semibold text-gray-900 mt-8 mb-4">1. Information We Collect</h2>
            
            <h3 className="text-lg font-medium text-gray-800 mt-6 mb-3">a) Personal Information:</h3>
            <ul className="list-disc pl-6 text-gray-600 mb-4 space-y-2">
              <li>Name, email address</li>
              <li>Company name</li>
              <li>Login credentials</li>
            </ul>

            <h3 className="text-lg font-medium text-gray-800 mt-6 mb-3">b) Employee Tracking Data:</h3>
            <ul className="list-disc pl-6 text-gray-600 mb-4 space-y-2">
              <li>GPS location during work sessions</li>
              <li>Check-in and check-out timestamps</li>
              <li>Break start and end times</li>
              <li>Idle and activity status</li>
            </ul>

            <h3 className="text-lg font-medium text-gray-800 mt-6 mb-3">c) Technical Information:</h3>
            <ul className="list-disc pl-6 text-gray-600 mb-6 space-y-2">
              <li>IP address</li>
              <li>Device type</li>
              <li>Browser and OS</li>
              <li>Usage logs</li>
            </ul>

            <h2 className="text-xl font-semibold text-gray-900 mt-8 mb-4">2. How We Use Data</h2>
            <p className="text-gray-600 mb-4">We use collected data to:</p>
            <ul className="list-disc pl-6 text-gray-600 mb-6 space-y-2">
              <li>Provide attendance tracking services</li>
              <li>Generate productivity reports</li>
              <li>Improve system accuracy</li>
              <li>Ensure platform security</li>
              <li>Provide customer support</li>
            </ul>
            <p className="text-gray-600 mb-6 font-medium">
              We do not sell personal or tracking data.
            </p>

            <h2 className="text-xl font-semibold text-gray-900 mt-8 mb-4">3. Legal Basis for Processing (GDPR)</h2>
            <p className="text-gray-600 mb-4">For EU users, processing is based on:</p>
            <ul className="list-disc pl-6 text-gray-600 mb-6 space-y-2">
              <li>Legitimate business interest</li>
              <li>Contractual necessity</li>
              <li>User and employee consent</li>
              <li>Legal compliance</li>
            </ul>

            <h2 className="text-xl font-semibold text-gray-900 mt-8 mb-4">4. GPS & Monitoring Transparency</h2>
            <p className="text-gray-600 mb-4">Location and activity tracking:</p>
            <ul className="list-disc pl-6 text-gray-600 mb-6 space-y-2">
              <li>Only occurs during active work sessions</li>
              <li>Stops when employees check out</li>
              <li>Is controlled by company administrators</li>
              <li>Requires employee awareness and consent</li>
            </ul>

            <h2 className="text-xl font-semibold text-gray-900 mt-8 mb-4">5. Data Sharing</h2>
            <p className="text-gray-600 mb-4">We may share data only with:</p>
            <ul className="list-disc pl-6 text-gray-600 mb-6 space-y-2">
              <li>Secure cloud hosting providers</li>
              <li>Payment processors</li>
              <li>Legal authorities if required</li>
            </ul>
            <p className="text-gray-600 mb-6">
              All partners follow strict confidentiality rules.
            </p>

            <h2 className="text-xl font-semibold text-gray-900 mt-8 mb-4">6. Data Retention</h2>
            <p className="text-gray-600 mb-4">Data is retained:</p>
            <ul className="list-disc pl-6 text-gray-600 mb-6 space-y-2">
              <li>As long as the account is active</li>
              <li>Or as required by legal obligations</li>
              <li>After account deletion, data is removed within 30 days</li>
            </ul>

            <h2 className="text-xl font-semibold text-gray-900 mt-8 mb-4">7. Data Security</h2>
            <p className="text-gray-600 mb-4">We use:</p>
            <ul className="list-disc pl-6 text-gray-600 mb-6 space-y-2">
              <li>Encrypted data transmission</li>
              <li>Secure servers</li>
              <li>Access control and authentication</li>
              <li>Regular security audits</li>
            </ul>
            <p className="text-gray-600 mb-6">
              However, no system is 100% secure.
            </p>

            <h2 className="text-xl font-semibold text-gray-900 mt-8 mb-4">8. User Rights (GDPR)</h2>
            <p className="text-gray-600 mb-4">You have the right to:</p>
            <ul className="list-disc pl-6 text-gray-600 mb-6 space-y-2">
              <li>Access your data</li>
              <li>Correct inaccurate data</li>
              <li>Request data deletion</li>
              <li>Restrict or object to processing</li>
              <li>Data portability</li>
            </ul>
            <p className="text-gray-600 mb-6">
              Requests can be sent to: <a href="mailto:support@trackly.net" className="text-violet-600 hover:underline">support@trackly.net</a>
            </p>

            <h2 className="text-xl font-semibold text-gray-900 mt-8 mb-4">9. Cookies</h2>
            <p className="text-gray-600 mb-4">Trackly uses cookies for:</p>
            <ul className="list-disc pl-6 text-gray-600 mb-6 space-y-2">
              <li>Session management</li>
              <li>Security</li>
              <li>Performance optimization</li>
            </ul>
            <p className="text-gray-600 mb-6">
              You may disable cookies in your browser, but functionality may be limited.
            </p>

            <h2 className="text-xl font-semibold text-gray-900 mt-8 mb-4">10. Children&apos;s Privacy</h2>
            <p className="text-gray-600 mb-6">
              Trackly is not intended for individuals under 18.
            </p>

            <h2 className="text-xl font-semibold text-gray-900 mt-8 mb-4">11. Changes to Policy</h2>
            <p className="text-gray-600 mb-6">
              We may update this Privacy Policy periodically. Continued use means acceptance of changes.
            </p>

            <h2 className="text-xl font-semibold text-gray-900 mt-8 mb-4">12. Contact Information</h2>
            <p className="text-gray-600 mb-2">For privacy-related concerns:</p>
            <p className="text-gray-600 mb-2">Trackly.net</p>
            <p className="text-gray-600">
              Email: <a href="mailto:support@trackly.net" className="text-violet-600 hover:underline">support@trackly.net</a>
            </p>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-white border-t border-gray-200 py-6 mt-12">
        <div className="max-w-4xl mx-auto px-4 text-center text-gray-500 text-sm">
          © {new Date().getFullYear()} {branding.platform_name}. All rights reserved.
        </div>
      </footer>
    </div>
  );
}
